import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Seo from '../../components/Seo';
import ShareBar from '../../components/ShareBar';
import GalleryGrid from '../../components/GalleryGrid';
import MediaCarousel from '../../components/MediaCarousel';
import FacebookPostEmbed from '../../components/FacebookPostEmbed';
import { PageLoader, ErrorState, Breadcrumbs } from '../../components/ui';
import { useAsync } from '../../lib/useAsync';
import { sanitize } from '../../lib/sanitize';
import { newsApi } from '../../api/resources';
import { assetUrl } from '../../api/client';
import { formatDate } from '../../lib/format';

/** Best display URL for a news featured image (handles absolute + stored paths). */
function newsImage(item) {
  const path = item?.featured_image_path;
  if (path && /^https?:\/\//i.test(path)) return path;
  return item?.featured_image_url || assetUrl(path);
}

export default function NewsDetail() {
  const { slug } = useParams();
  const { data: item, loading, error, reload } = useAsync(() => newsApi.get(slug), [slug]);
  const [carouselIndex, setCarouselIndex] = useState(null);

  if (loading) return <PageLoader label="Loading article…" />;
  if (error)
    return (
      <div className="container-page py-16">
        <ErrorState error={error} onRetry={reload} />
      </div>
    );
  if (!item) return null;

  const img = newsImage(item);
  const gallery = item.gallery || [];
  // Facebook post embeds are shown inline below the body; everything else
  // stays in the photo grid + carousel (both get the same filtered array, so
  // carousel indexes line up with the grid).
  const posts = gallery.filter((g) => g.provider === 'facebook_post');
  const media = gallery.filter((g) => g.provider !== 'facebook_post');

  return (
    <>
      <Seo title={item.title} description={item.excerpt} image={img} type="article" />

      <article className="pb-16">
        {img ? (
          <header className="relative overflow-hidden">
            <img
              src={img}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="hero-overlay" />
            <div className="container-page relative max-w-4xl py-20 sm:py-28">
              <p className="eyebrow mb-3 text-gold-400">News &amp; Highlights</p>
              {item.published_at && (
                <p className="mb-3 text-sm text-cream-200">{formatDate(item.published_at)}</p>
              )}
              <h1 className="font-serif text-3xl font-bold leading-tight text-white sm:text-5xl">
                {item.title}
              </h1>
            </div>
          </header>
        ) : (
          <header className="border-b border-cream-300 bg-cream-100">
            <div className="container-page max-w-3xl py-14 sm:py-20 text-center">
              <p className="eyebrow mb-3 text-maroon-700">News &amp; Highlights</p>
              <h1 className="heading-brand text-4xl sm:text-5xl">{item.title}</h1>
              {item.published_at && (
                <p className="mt-4 text-sm text-ink-muted">{formatDate(item.published_at)}</p>
              )}
            </div>
          </header>
        )}

        <div className="container-page max-w-3xl pt-8">
          <Breadcrumbs
            items={[{ label: 'Home', to: '/' }, { label: 'News', to: '/news' }, { label: item.title }]}
          />

          {item.excerpt && (
            <div className="mt-6 rounded-2xl bg-cream-100 p-6 ring-1 ring-cream-300">
              <p className="text-lg italic leading-relaxed text-ink-soft">{item.excerpt}</p>
            </div>
          )}

          <div
            className="rich-content mt-6"
            dangerouslySetInnerHTML={{ __html: sanitize(item.body) || '<p>No content.</p>' }}
          />

          {posts.length > 0 && (
            <section className="mt-10" aria-labelledby="news-facebook-heading">
              <h2 id="news-facebook-heading" className="heading-brand text-xl">
                From our Facebook Page
              </h2>
              <div className="mt-4 space-y-6">
                {posts.map((p) => (
                  <FacebookPostEmbed key={p.id} embedUrl={p.embed_url} url={p.url} title={`Facebook post: ${item.title}`} />
                ))}
              </div>
            </section>
          )}

          {media.length > 0 && (
            <section className="mt-10" aria-labelledby="news-gallery-heading">
              <h2 id="news-gallery-heading" className="heading-brand text-xl">
                Gallery
              </h2>
              <p className="mt-1 text-sm text-ink-muted">
                {media.length} {media.length === 1 ? 'item' : 'items'}. Select any to view.
              </p>
              <div className="mt-4">
                <GalleryGrid items={media} onOpen={setCarouselIndex} />
              </div>
            </section>
          )}

          <hr className="my-8 border-cream-300" />
          <ShareBar title={item.title} />

          <div className="mt-10">
            <Link to="/news" className="btn-ghost">
              ← Back to all news
            </Link>
          </div>
        </div>
      </article>

      <MediaCarousel
        items={media}
        index={carouselIndex}
        onClose={() => setCarouselIndex(null)}
        onIndexChange={setCarouselIndex}
      />
    </>
  );
}
